// A company's look, used by the chat widget and the booking page (and later
// the dashboard theme). Mirrors internal/modules/brand.

export interface Brand {
  primary?: string;
  on_primary?: string;
  background?: string;
  surface?: string;
  text?: string;
  font?: string;
  corners?: "sharp" | "soft" | "round";
  logo?: string;
}

export const brandDefaults: Required<Omit<Brand, "logo" | "on_primary">> = {
  primary: "#1f2937", background: "#f7f7f5", surface: "#ffffff", text: "#1b1d21", font: "system", corners: "soft",
};

export const fontOptions: [string, string, string][] = [
  ["system", "System default", `ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Tahoma, sans-serif`],
  ["almarai", "Almarai", `"Almarai", system-ui, sans-serif`],
  ["cairo", "Cairo", `"Cairo", system-ui, sans-serif`],
  ["plex", "IBM Plex Sans (+ Arabic)", `"IBM Plex Sans", "IBM Plex Sans Arabic", system-ui, sans-serif`],
  ["inter", "Inter", `"Inter", "IBM Plex Sans Arabic", system-ui, sans-serif`],
  ["noto", "Noto Sans (+ Kufi Arabic)", `"Noto Sans", "Noto Kufi Arabic", system-ui, sans-serif`],
  ["playfair", "Playfair Display (elegant)", `"Playfair Display", "Noto Naskh Arabic", Georgia, serif`],
  ["tajawal", "Tajawal", `"Tajawal", system-ui, sans-serif`],
];

export const radii = { sharp: [2, 4], soft: [8, 12], round: [14, 20] } as const;

// Ready-made looks to start from.
export const brandPresets: { name: string; brand: Brand }[] = [
  { name: "Classic", brand: { primary: "#1f2937", background: "#f7f7f5", surface: "#ffffff", text: "#1b1d21", corners: "soft" } },
  { name: "Gold on black", brand: { primary: "#c8a24b", background: "#0e0e10", surface: "#17171a", text: "#f4f1ea", corners: "soft" } },
  { name: "Navy", brand: { primary: "#1d3a6e", background: "#f3f5f9", surface: "#ffffff", text: "#14213d", corners: "soft" } },
  { name: "Clinic teal", brand: { primary: "#0f766e", background: "#f2f8f7", surface: "#ffffff", text: "#12302d", corners: "round" } },
  { name: "Burgundy", brand: { primary: "#7a1f3d", background: "#faf6f4", surface: "#ffffff", text: "#2a1418", corners: "soft" } },
  { name: "Sand", brand: { primary: "#9a6b3f", background: "#f8f3ec", surface: "#fffdf9", text: "#2e241b", corners: "round" } },
];

const hex = /^#[0-9a-f]{6}$/i;

function rgb(h: string): [number, number, number] {
  const v = parseInt(h.slice(1), 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}
function lum(h: string) {
  const f = (c: number) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  const [r, g, b] = rgb(h);
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
export function contrast(a: string, b: string) {
  const x = lum(a), y = lum(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
export const readableOn = (bg: string) => (contrast(bg, "#ffffff") >= contrast(bg, "#111111") ? "#ffffff" : "#111111");
function mix(a: string, b: string, t: number) {
  const [ar, ag, ab] = rgb(a), [br, bg, bb] = rgb(b);
  const m = (x: number, y: number) => Math.round(x + (y - x) * t).toString(16).padStart(2, "0");
  return `#${m(ar, br)}${m(ag, bg)}${m(ab, bb)}`;
}

export function resolveBrand(b: Brand) {
  const pick = (v: string | undefined, d: string) => (v && hex.test(v) ? v.toLowerCase() : d);
  const primary = pick(b.primary, brandDefaults.primary);
  const surface = pick(b.surface, brandDefaults.surface);
  const text = pick(b.text, brandDefaults.text);
  const background = pick(b.background, brandDefaults.background);
  const font = fontOptions.find((f) => f[0] === b.font) ?? fontOptions[0];
  const r = radii[b.corners ?? "soft"] ?? radii.soft;
  return {
    primary, background, surface, text, onPrimary: pick(b.on_primary, readableOn(primary)),
    muted: mix(text, surface, 0.38), line: mix(text, surface, 0.86), fontStack: font[2], radius: r[0], radiusLg: r[1],
  };
}

export function brandWarnings(b: Brand): string[] {
  const r = resolveBrand(b);
  const out: string[] = [];
  if (contrast(r.text, r.surface) < 4.5) out.push("Text is hard to read on the panel color.");
  if (contrast(r.text, r.background) < 4.5) out.push("Text is hard to read on the background color.");
  if (contrast(r.onPrimary, r.primary) < 4.5) out.push("Button text is hard to read on the main color.");
  return out;
}
